import { Box, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SurveyTrendLineChartComponent } from './survey-trend-over-time-line-chart.component';
import { useGetAnalyticsSurveysTrendOverTimeQuery } from '../../../../store/api/analytics.api';
import dayjs from 'dayjs';
import { FilterValuesStrings } from '../../pages/analytics.page';
import { FadeSpinner } from '../../../_core/components/_ui/fade-spinner';
import { AnalyticsBaseQueryParams } from '@families-link/shared';

export type SurveyTrendFullDateChartProps = {
	transformedFilterValues: FilterValuesStrings;
	currentDistrictId?: string | null;
	authToken?: string;
};

export const SurveyTrendFullDateChart: FC<SurveyTrendFullDateChartProps> = (props) => {
	const { currentDistrictId, transformedFilterValues, authToken } = props;
	const [t] = useTranslation();
	const normalArgs: AnalyticsBaseQueryParams = {
		start:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[0]
				? dayjs(transformedFilterValues.duration[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		end:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[1]
				? dayjs(transformedFilterValues.duration[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		schools: transformedFilterValues?.schoolIds,
		district: currentDistrictId,
		districts: transformedFilterValues?.districtIds,
		services: transformedFilterValues?.serviceIds,
		programs: transformedFilterValues?.programIds,
		providers: transformedFilterValues?.providerIds,
		languages: transformedFilterValues?.languages,
		engagementStatuses: transformedFilterValues?.engagementStatuses,
		engagementTypes: transformedFilterValues?.engagementTypes,
	};

	const authenticatedNormalArgs = useMemo(
		() => ({
			queryParams: normalArgs,
			authToken,
		}),
		[normalArgs, authToken]
	);

	const providersQuery = useGetAnalyticsSurveysTrendOverTimeQuery(authenticatedNormalArgs, {
		refetchOnMountOrArgChange: true,
	});

	const data = providersQuery.data;
	const isFetching = providersQuery.isFetching;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				width: '100%',
				height: '100%',
				gap: 2,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
				<Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#797F92' }}>Surveys Trend Over Time</Typography>
			</Box>
			{isFetching ? (
				<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
					<FadeSpinner size={51} height={15} width={6} borderRadius={3} />
				</Box>
			) : (
				<Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					{data && data.length > 0 ? (
						<SurveyTrendLineChartComponent data={data} />
					) : (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}>
								No data found for the chart
							</Typography>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};
